import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import { Toaster } from "./components/ui/sonner";
import Landing from "./pages/Landing";
import About from "./pages/About";
import Login from "./pages/Login";
import Dashboard from "./pages/Dashboard";
import LiveDashboard from "./pages/LiveDashboard";
import Jobs from "./pages/Jobs";
import JobDetail from "./pages/JobDetail";
import Candidates from "./pages/Candidates";
import InterviewLinks from "./pages/InterviewLinks";
import InterviewRoom from "./pages/InterviewRoom";
import Reports from "./pages/Reports";
import Billing from "./pages/Billing";
import DemoInterview from "./pages/DemoInterview";
import TestInterview from "./pages/TestInterview";
import Terms from "./pages/Terms";
import Privacy from "./pages/Privacy";
import AuthCallback from "./components/AuthCallback";
import ProtectedRoute from "./components/ProtectedRoute";
import "./App.css";
import AdminLogin from "./pages/admin/AdminLogin";
import AdminDashboard from "./pages/admin/AdminDashboard";
import AdminUsers from "./pages/admin/AdminUsers";
import AdminProtectedRoute from "./components/admin/AdminProtectedRoute";
import AdminEmployees from "./pages/admin/AdminEmployees";
import AdminBilling from "./pages/admin/AdminBilling";
import AdminInterviews from "./pages/admin/AdminInterviews";
import AdminLogs from "./pages/admin/AdminLogs";
import AdminTestimonials from "./pages/admin/AdminTestimonials";
import AdminAIMonitor from "./pages/admin/AdminAIMonitor";
import AdminProfile from "./pages/admin/AdminProfile";
import LiveInterviews from "./pages/LiveInterviews";
import { ThemeProvider } from "./contexts/ThemeContext";
import { GoogleOAuthProvider } from "@react-oauth/google";

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH

function AppRouter() {
  const location = useLocation();
  
  // Check URL fragment for session_id (synchronous check before routes)
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }
  
  return (
    <Routes>
      <Route path="/" element={<Landing />} />
      <Route path="/about" element={<About />} />
      <Route path="/login" element={<Login />} />
      <Route path="/terms" element={<Terms />} />
      <Route path="/privacy" element={<Privacy />} />
      <Route path="/interview/:token" element={<InterviewRoom />} />
      <Route 
        path="/dashboard" 
        element={
          <ProtectedRoute>
            <Dashboard />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/dashboard/live" 
        element={
          <ProtectedRoute>
            <LiveDashboard />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/jobs" 
        element={
          <ProtectedRoute>
            <Jobs />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/jobs/:jobId" 
        element={
          <ProtectedRoute>
            <JobDetail />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/candidates" 
        element={
          <ProtectedRoute>
            <Candidates />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/interview-links" 
        element={
          <ProtectedRoute>
            <InterviewLinks />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/reports" 
        element={
          <ProtectedRoute>
            <Reports />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/billing" 
        element={
          <ProtectedRoute>
            <Billing />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/live-interviews" 
        element={
          <ProtectedRoute>
            <LiveInterviews />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/demo" 
        element={
          <ProtectedRoute>
            <DemoInterview />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/test-interview" 
        element={
          <ProtectedRoute>
            <TestInterview />
          </ProtectedRoute>
        } 
      />
      {/* Admin Routes */}
      <Route path="/admin/auth" element={<AdminLogin />} />
      <Route path="/admin" element={<AdminProtectedRoute />}>
        <Route index element={<AdminDashboard />} />
        <Route path="users" element={<AdminUsers />} />
        <Route path="employees" element={<AdminEmployees />} />
        <Route path="billing" element={<AdminBilling />} />
        <Route path="interviews" element={<AdminInterviews />} />
        <Route path="testimonials" element={<AdminTestimonials />} />
        <Route path="logs" element={<AdminLogs />} />
        <Route path="ai-monitor" element={<AdminAIMonitor />} />
              <Route path="profile" element={<AdminProfile />} />
        <Route path="*" element={<Navigate to="/admin" replace />} />
      </Route>
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>

  );
}

function AppContent() {
  const location = useLocation();
  const publicRoutes = ['/', '/about', '/terms', '/privacy', '/login'];
  const isPublicPage = publicRoutes.includes(location.pathname) || location.pathname.startsWith('/interview/');
  
  return (
    <div className={`min-h-screen transition-colors ${isPublicPage ? 'bg-canvas' : 'bg-canvas dark:bg-zinc-950'}`}>
      <div className="noise-overlay" />
      <AppRouter />
      <Toaster richColors position="top-right" />
    </div>
  );
}

function App() {
  return (
    <GoogleOAuthProvider clientId={process.env.REACT_APP_GOOGLE_CLIENT_ID}>
      <ThemeProvider>
        <BrowserRouter>
          <AppContent />
        </BrowserRouter>
      </ThemeProvider>
    </GoogleOAuthProvider>
  );
}

export default App;
